import { AuthLayout, SecretInput, Spinner, StatusBadge } from '@repo/ui'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { KeyRoundIcon, ShieldCheckIcon } from 'lucide-react'
import { FormEvent, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAdminAuth } from '@/hooks/use-admin-auth'
import { configureAdminApi, fetchAdminOverview } from '@/lib/api'
import { environmentOf } from '@/lib/environment'

type LoginSearch = {
  returnTo?: string
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    returnTo: typeof search.returnTo === 'string' ? search.returnTo : undefined,
  }),
  component: LoginPage,
})

function LoginPage() {
  const { credentials, setCredentials } = useAdminAuth()
  const navigate = useNavigate()
  const [apiUrl, setApiUrl] = useState(
    credentials?.apiUrl ??
      import.meta.env.VITE_API_URL ??
      'http://localhost:5050'
  )
  const [token, setToken] = useState(credentials?.token ?? '')

  const mutation = useMutation({
    mutationFn: async () => {
      configureAdminApi({ apiUrl, token })
      await fetchAdminOverview()
    },
    onSuccess: () => {
      setCredentials({ apiUrl, token })
      toast.success('Acesso admin validado')
      void navigate({ to: '/' })
    },
  })

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    mutation.mutate()
  }

  const environment = environmentOf(apiUrl)

  return (
    <AuthLayout
      logo={
        <img
          src="/agrosdr-logo.png"
          alt="AgroSDR"
          className="h-16 w-auto mix-blend-multiply"
        />
      }
      title="Entrar no admin"
      subtitle="Acesso operacional restrito à equipe AgroSDR."
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="apiUrl">URL da API</Label>
            <StatusBadge tone={environment.tone} dot>
              {environment.label}
            </StatusBadge>
          </div>
          <Input
            id="apiUrl"
            value={apiUrl}
            onChange={(event) => setApiUrl(event.target.value)}
            placeholder="https://api.agrosdr.com.br"
            autoComplete="url"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="token">Chave admin</Label>
          <SecretInput
            id="token"
            visibilityLabel="chave admin"
            value={token}
            onChange={(event) => setToken(event.target.value)}
            placeholder="ADMIN_API_KEY"
            autoComplete="current-password"
            required
          />
        </div>

        <Button className="w-full" disabled={mutation.isPending}>
          {mutation.isPending ? (
            <Spinner />
          ) : (
            <KeyRoundIcon className="size-4" />
          )}
          Entrar
        </Button>

        <p className="text-muted-foreground flex gap-2 text-xs">
          <ShieldCheckIcon className="mt-0.5 size-3.5 shrink-0" />A chave fica
          apenas neste navegador. Nunca coloque ADMIN_API_KEY em variáveis Vite
          públicas.
        </p>
      </form>
    </AuthLayout>
  )
}
